import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';
import {
    CardImportReport, CardStatementDetail, CardStatementDTO, CardTransactionDTO, CardTransactionStatus,
    CorporateCardDTO,
} from '../interfaces/expense.interface';

const auth = () => ({ headers: AuthService.getAuthHeader() });
const inst = () => AuthService.getSelectedInstitution()!!;

export interface CardInput { holderUserId: string; bankName: string; last4: string; liabilityAccountId: string }

/** W3-U corporate cards, statements and card transactions. */
export const ExpenseCardService = {
    listCards: async (): Promise<CorporateCardDTO[]> => (await axios.get(ENDPOINTS.EXPENSE_CARD.CARDS(inst()), auth())).data,
    createCard: async (req: CardInput): Promise<CorporateCardDTO> =>
        (await axios.post(ENDPOINTS.EXPENSE_CARD.CARDS(inst()), { ...req, institutionId: inst() }, auth())).data,
    updateCard: async (id: string, req: CardInput): Promise<CorporateCardDTO> =>
        (await axios.put(ENDPOINTS.EXPENSE_CARD.CARD(inst(), id), { ...req, institutionId: inst() }, auth())).data,
    setCardActive: async (id: string, active: boolean): Promise<CorporateCardDTO> =>
        (await axios.put(ENDPOINTS.EXPENSE_CARD.CARD_ACTIVE(inst(), id), null, { ...auth(), params: { active } })).data,

    previewImport: async (cardId: string, file: File): Promise<CardImportReport> => {
        const form = new FormData();
        form.append('file', file);
        return (await axios.post(ENDPOINTS.EXPENSE_CARD.IMPORT_PREVIEW(inst(), cardId), form, auth())).data;
    },
    commitImport: async (cardId: string, file: File): Promise<{ statementId: string; rowCount: number; totalAmount: number }> => {
        const form = new FormData();
        form.append('file', file);
        return (await axios.post(ENDPOINTS.EXPENSE_CARD.IMPORT_COMMIT(inst(), cardId), form, auth())).data;
    },

    listStatements: async (): Promise<CardStatementDTO[]> => (await axios.get(ENDPOINTS.EXPENSE_CARD.STATEMENTS(inst()), auth())).data,
    getStatement: async (id: string): Promise<CardStatementDetail> => (await axios.get(ENDPOINTS.EXPENSE_CARD.STATEMENT(inst(), id), auth())).data,
    payStatement: async (id: string, paymentAccountId: string, paymentDate?: string): Promise<CardStatementDTO> =>
        (await axios.post(ENDPOINTS.EXPENSE_CARD.STATEMENT_PAY(inst(), id), { paymentAccountId, paymentDate }, auth())).data,

    listTransactions: async (status?: CardTransactionStatus): Promise<CardTransactionDTO[]> =>
        (await axios.get(ENDPOINTS.EXPENSE_CARD.TRANSACTIONS(inst()), { ...auth(), params: status ? { status } : {} })).data,
    listMyTransactions: async (status?: CardTransactionStatus): Promise<CardTransactionDTO[]> =>
        (await axios.get(ENDPOINTS.EXPENSE_CARD.MY_TRANSACTIONS(inst()), { ...auth(), params: status ? { status } : {} })).data,
    markPersonal: async (id: string): Promise<CardTransactionDTO> =>
        (await axios.post(ENDPOINTS.EXPENSE_CARD.PERSONAL(inst(), id), null, auth())).data,
    unmarkPersonal: async (id: string): Promise<CardTransactionDTO> =>
        (await axios.delete(ENDPOINTS.EXPENSE_CARD.PERSONAL(inst(), id), auth())).data,
    writeToReceivable: async (id: string): Promise<CardTransactionDTO> =>
        (await axios.post(ENDPOINTS.EXPENSE_CARD.WRITE_TO_RECEIVABLE(inst(), id), null, auth())).data,
};
